/**
 * What forging shows on a weapon (data/forge), growing step by step:
 *
 * - the + (ตีบวก): +1…+3 a polished sheen with a glint running up the edge; +4…+6 a pale azure
 *   charge, crackling from +5; +7…+9 blue lightning (สายฟ้าคราม), +10…+14 red (สายฟ้าโลหิต),
 *   +15 black (สายฟ้าทมิฬ) — more bolts, longer arcs, sparks and a surge every few seconds the
 *   higher it goes, and from +12 a glow on the ground;
 * - the element (ตีธาตุ), level 1…8: a faint tint of its colour and now and then a mote of its
 *   matter at first; then its own shimmer (heat, venom, dust, wet, moving air); from level 5 motes
 *   of it circling the blade; from 7 trails and a glow on the ground; at 8 everything.
 *
 * Realistic pixel art like the figures: hard half-pixels (the rig's resolution), no smoothing, lit
 * from the upper right. Everything is drawn in a frame's art pixels along its `BladeLine`s, so it
 * follows the blade through every strike, in two passes — `back` before the figure, `front` after.
 *
 * Kept cheap for crowds: whatever lies along the blade (haze, bolts, tints, shimmer) is painted once
 * into a small canvas in the blade's own space — keyed by its length in whole pixels and a few
 * flicker variants — and laid along the blade with one rotated `drawImage`; motes are cached
 * sprites; only a handful of loose particles are drawn as single pixels each frame. Animation is a
 * pure function of time, except the afterimage of a swing (`trail`), which remembers the last blade.
 */
import { type ForgeElement, type WeaponForge } from '@nine/shared';
import { canvas } from './pixel';
import type { BladeLine } from './rig/weapons';

type G = CanvasRenderingContext2D;
export type FxLayer = 'back' | 'front';

/** Whether a forge shows anything at all: every + and every element level does. */
export const forgeShows = (f: WeaponForge | null | undefined): f is WeaponForge =>
  !!f && (f.plus > 0 || (!!f.el && f.lv > 0));

/** Size of one effect pixel in art pixels: the rig draws at double resolution. */
const P = 0.5;
const TAU = Math.PI * 2;

/** A hard pixel (or a `w`×`h` block of them) snapped to the half-pixel grid. */
function dot(g: G, x: number, y: number, col: string, w = P, h = w): void {
  g.fillStyle = col;
  g.fillRect(Math.round(x / P) * P, Math.round(y / P) * P, w, h);
}

/** A stable random number in [0, 1) from a few integers. */
function hash(a: number, b = 0, c = 0): number {
  let h =
    Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x27d4eb2f, 0xc2b2ae35) ^ Math.imul(c, 0x165667b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

const frac = (v: number) => v - Math.floor(v);

/** Where the blade proper starts past the grip, art pixels from the hand. */
const bladeStart = (L: number) => Math.min(3, L * 0.15);

interface Pt {
  x: number;
  y: number;
}

/** A point `k` art pixels along a blade and `side` across it. */
function along(b: BladeLine, k: number, side = 0): Pt {
  return { x: b.hx + b.dx * k - b.dy * side, y: b.hy + b.dy * k + b.dx * side };
}

// ── strips: what lies along a blade, painted once in the blade's own space ──

/** Paints a pixel at (`x` along, `y` across) the blade, in art pixels. */
type Px = (x: number, y: number, col: string, w?: number, h?: number) => void;

interface Strip {
  c: HTMLCanvasElement;
  /** Art pixels before the hand, and either side of the blade, the canvas reaches. */
  m: number;
  r: number;
}

const STRIPS = new Map<string, Strip>();
/** Strips are small, but a crowd of odd blade lengths adds up: start over past this many. */
const STRIP_LIMIT = 1500;

function stripOf(key: string, L: number, m: number, r: number, paint: (px: Px) => void): Strip {
  let s = STRIPS.get(key);
  if (s) return s;
  if (STRIPS.size >= STRIP_LIMIT) STRIPS.clear();
  const c = canvas(Math.ceil((L + m * 2) / P) + 1, Math.ceil((r * 2) / P) + 1);
  const g = c.getContext('2d')!;
  const px: Px = (x, y, col, w = P, h = w) => {
    g.fillStyle = col;
    g.fillRect(
      Math.round((x + m) / P),
      Math.round((y + r) / P),
      Math.max(1, Math.round(w / P)),
      Math.max(1, Math.round(h / P)),
    );
  };
  paint(px);
  s = { c, m, r };
  STRIPS.set(key, s);
  return s;
}

/** How many strips and sprites are cached (for the perf export). */
export const forgeFxCache = (): { strips: number; sprites: number } => ({
  strips: STRIPS.size,
  sprites: SPRITES.size,
});

/** Lays a strip along the blade (the context already turned to the blade: see `toBlade`). */
function lay(g: G, s: Strip, alpha = 1): void {
  if (alpha <= 0.01) return;
  const a = g.globalAlpha;
  if (alpha < 1) g.globalAlpha = a * alpha;
  g.drawImage(s.c, -s.m, -s.r, s.c.width * P, s.c.height * P);
  g.globalAlpha = a;
}

/** Turns the context so x runs along the blade from the hand and y across it. */
function toBlade(g: G, b: BladeLine): void {
  g.transform(b.dx, b.dy, -b.dy, b.dx, b.hx, b.hy);
}

// ── lightning ──

type BoltTier = 'static' | 'blue' | 'red' | 'black';

interface Bolt {
  /** The haze round the blade, the glow round a bolt, its core, and the brightest flecks. */
  haze: string;
  glow: string;
  core: string;
  fleck: string;
  /** A thin bright rim outside the glow (black lightning, so it reads against dark ground). */
  rim?: string;
  /** How far an arc leaps off the blade. */
  reach: number;
}

const BOLTS: Record<BoltTier, Bolt> = {
  // The crackle of a +5/+6 blade: short, pale, hugging the steel.
  static: {
    haze: 'rgba(120,180,240,0.2)',
    glow: 'rgba(130,190,255,0.55)',
    core: '#e4f2ff',
    fleck: '#ffffff',
    reach: 3,
  },
  blue: {
    haze: 'rgba(60,140,255,0.22)',
    glow: 'rgba(80,170,255,0.75)',
    core: '#eaf6ff',
    fleck: '#ffffff',
    reach: 7,
  },
  red: {
    haze: 'rgba(220,30,30,0.24)',
    glow: 'rgba(255,60,40,0.8)',
    core: '#fff0e2',
    fleck: '#ffd8a8',
    reach: 9,
  },
  black: {
    // Black lightning: a dark core in a violet glow, in a haze of smoke.
    haze: 'rgba(20,6,30,0.3)',
    glow: '#1a0c26',
    core: '#000000',
    fleck: '#efe4ff',
    rim: 'rgba(170,110,255,0.7)',
    reach: 11,
  },
};

/** The rim and glow round a pixel line from a to b (the core goes over them: see `path`). */
function zapGlow(px: Px, a: Pt, b: Pt, bolt: Bolt): void {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) / P));
  if (bolt.rim)
    for (let i = 0; i <= n; i++)
      px(a.x + ((b.x - a.x) * i) / n - P * 1.5, a.y + ((b.y - a.y) * i) / n - P * 1.5, bolt.rim, P * 4);
  for (let i = 0; i <= n; i++)
    px(a.x + ((b.x - a.x) * i) / n - P, a.y + ((b.y - a.y) * i) / n - P, bolt.glow, P * 3);
}

/** A jagged path from `from` toward `to`, in steps of about `step` art pixels, jittered by `jit`. */
function jagged(from: Pt, to: Pt, seed: number, step: number, jit: number): Pt[] {
  const len = Math.hypot(to.x - from.x, to.y - from.y);
  const n = Math.max(2, Math.round(len / step));
  const nx = -(to.y - from.y) / (len || 1);
  const ny = (to.x - from.x) / (len || 1);
  const pts = [from];
  for (let i = 1; i < n; i++) {
    const k = i / n;
    const off = (hash(seed, i) - 0.5) * 2 * jit;
    pts.push({ x: from.x + (to.x - from.x) * k + nx * off, y: from.y + (to.y - from.y) * k + ny * off });
  }
  pts.push(to);
  return pts;
}

function path(px: Px, pts: Pt[], bolt: Bolt, thick: boolean): void {
  // Glow of every segment first, so a later segment's glow never covers an earlier core.
  for (let i = 1; i < pts.length; i++) zapGlow(px, pts[i - 1]!, pts[i]!, bolt);
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) / P));
    for (let j = 0; j <= n; j++)
      px(a.x + ((b.x - a.x) * j) / n, a.y + ((b.y - a.y) * j) / n, bolt.core, thick ? P * 2 : P);
  }
}

/** Bolt shapes kept per blade length: each flicker picks one. */
const BOLT_VARIANTS = 6;

/** One bolt crawling along a blade `L` long, and (`arc`) an arc leaping off it, forking. */
function boltStrip(tier: BoltTier, L: number, v: number, arc: boolean, thick: boolean): Strip {
  const bolt = BOLTS[tier];
  return stripOf(`bolt:${tier}:${L}:${v}:${+arc}:${+thick}`, L, 4, bolt.reach + 5, (px) => {
    const s = v * 977 + L * 31 + (arc ? 7 : 0) + (tier === 'static' ? 3 : 0);
    const start = bladeStart(L);
    const small = tier === 'static';
    const span = small ? 2 + hash(s, 2) * 3 : 4 + hash(s, 2) * L * 0.5;
    const k0 = start + hash(s, 1) * Math.max(0.5, small ? L - start - span : (L - start) * 0.6);
    const k1 = Math.min(L + 1, k0 + span);
    const side = (hash(s, 3) - 0.5) * 2 * (small ? 0.7 : 1);
    path(
      px,
      jagged({ x: k0, y: side }, { x: k1, y: -side }, s, small ? 1.4 : 2.2, small ? 0.9 : 1.6),
      bolt,
      thick,
    );
    if (!arc) return;
    const k = start + hash(s, 5) * (L - start);
    const out = (hash(s, 6) < 0.5 ? -1 : 1) * (3 + hash(s, 8) * bolt.reach);
    const end = { x: k + (hash(s, 9) - 0.3) * 4, y: out };
    const pts = jagged({ x: k, y: 0 }, end, s + 99, 2, 1.4);
    path(px, pts, bolt, false);
    const fork = pts[Math.floor(pts.length / 2)]!;
    path(px, jagged(fork, { x: k + (hash(s, 10) - 0.5) * 6, y: out * 0.6 }, s + 7, 1.8, 1), bolt, false);
    px(end.x - P, end.y - P, bolt.fleck, P * 2);
  });
}

/** The edge lit by the charge in flecks (`v` < 4), or all of it in a flare (`v` = 4), and the point's spark. */
function chargeStrip(tier: BoltTier, L: number, v: number): Strip {
  const bolt = BOLTS[tier];
  return stripOf(`charge:${tier}:${L}:${v}`, L, 3, 3, (px) => {
    const start = bladeStart(L);
    const flare = v === 4;
    for (let k = start; k <= L; k += P)
      if (flare || hash(v * 53 + L, Math.round(k * 2)) < 0.3) px(k, 0, flare ? bolt.fleck : bolt.core);
    if (v % 2 === 0) {
      px(L + 1 - P, 0, bolt.fleck);
      px(L + 1 + P, 0, bolt.fleck);
      px(L + 1, -P, bolt.fleck);
      px(L + 1, P, bolt.fleck);
    }
  });
}

/** The look of the steel round the blade before lightning: a polished sheen, or an azure charge. */
type Sheen = 'steel' | 'azure' | BoltTier;

/** The air round the blade: a sheen line and a haze a pixel or two wide, roughened. */
function hazeStrip(sheen: Sheen, L: number): Strip {
  return stripOf(`haze:${sheen}:${L}`, L, 3, 4, (px) => {
    const start = bladeStart(L);
    if (sheen === 'steel') {
      // Polished steel: only a pale cool halo, tight to the blade.
      for (let k = start; k <= L; k += P) {
        px(k - P, -1, 'rgba(214,226,240,0.2)', P * 3, P * 4);
        px(k, -P, 'rgba(230,238,250,0.3)', P, P * 2);
      }
      return;
    }
    if (sheen === 'azure') {
      for (let k = start; k <= L + 0.5; k += P) {
        const w = 1 + (hash(L, Math.round(k * 2)) < 0.2 ? P : 0);
        px(k - w, -w, 'rgba(110,170,240,0.22)', w * 2 + P);
        px(k - P, -P, 'rgba(170,215,255,0.4)', P * 3);
      }
      return;
    }
    const bolt = BOLTS[sheen];
    for (let k = start; k <= L; k += P) {
      const w = 1.25 + (hash(L * 3, Math.round(k * 2)) < 0.15 ? 1 : 0);
      px(k - w, -w, bolt.haze, w * 2 + P);
    }
  });
}

/** The afterimage a swing leaves: the blade's line in light. */
function ghostStrip(col: string, core: string, L: number): Strip {
  return stripOf(`ghost:${col}:${core}:${L}`, L, 2, 2, (px) => {
    for (let k = bladeStart(L); k <= L + 0.5; k += P) {
      px(k, -P, col, P, P * 3);
      px(k, 0, core);
    }
  });
}

/** How a + shows: the sheen and how strong, its bolts, glint, sparks, surge and ground glow. */
interface PlusLook {
  sheen: Sheen;
  sheenA: number;
  bolt: BoltTier | null;
  /** Bolts at once, the chance one leaps off as an arc, and (static) the chance any shows. */
  bolts: number;
  leap: number;
  chance: number;
  /** New bolts per second. */
  rate: number;
  /** Seconds between glints running up the edge (0: none). */
  glint: number;
  sparks: number;
  /** How hard the blade surges every couple of seconds (0 … 1). */
  surge: number;
  ground: GroundKind | null;
  groundA: number;
  smoke: boolean;
}

export function plusLook(plus: number): PlusLook {
  const p = Math.max(0, Math.min(15, Math.round(plus)));
  const base: PlusLook = {
    sheen: 'steel',
    sheenA: 0,
    bolt: null,
    bolts: 0,
    leap: 0,
    chance: 1,
    rate: 14,
    glint: 0,
    sparks: 0,
    surge: 0,
    ground: null,
    groundA: 0,
    smoke: false,
  };
  if (p <= 0) return base;
  if (p <= 3) return { ...base, sheenA: 0.4 + p * 0.2, glint: 3.6 - p * 0.6 };
  if (p <= 6)
    return {
      ...base,
      sheen: 'azure',
      sheenA: 0.5 + (p - 4) * 0.25,
      glint: 1.8,
      bolt: p >= 5 ? 'static' : null,
      bolts: 1,
      chance: p === 5 ? 0.22 : 0.45,
      rate: 9,
      sparks: p - 4,
      surge: 0.25 + (p - 4) * 0.15,
    };
  if (p <= 9)
    return {
      ...base,
      sheen: 'blue',
      sheenA: 0.75 + (p - 7) * 0.12,
      glint: 1.6,
      bolt: 'blue',
      bolts: p === 7 ? 1 : 2,
      leap: 0.2 + (p - 7) * 0.15,
      rate: 13 + (p - 7),
      sparks: 2 + (p - 7) * 0.5,
      surge: 0.6,
    };
  if (p <= 14)
    return {
      ...base,
      sheen: 'red',
      sheenA: 0.8 + (p - 10) * 0.05,
      glint: 1.4,
      bolt: 'red',
      bolts: p <= 11 ? 2 : 3,
      leap: 0.35 + (p - 10) * 0.08,
      rate: 15 + (p - 10) * 0.5,
      sparks: 3 + (p - 10) * 0.5,
      surge: 0.7 + (p - 10) * 0.06,
      ground: p >= 12 ? 'red' : null,
      groundA: (p - 11) * 0.3,
    };
  return {
    ...base,
    sheen: 'black',
    sheenA: 1,
    glint: 1.2,
    bolt: 'black',
    bolts: 4,
    leap: 0.65,
    rate: 18,
    sparks: 4,
    surge: 1,
    ground: 'black',
    groundA: 1,
    smoke: true,
  };
}

/** The surge: a sharp swell every 1.8 s or so (0 … 1). */
function surgeAt(t: number, seed: number): number {
  const v = Math.sin(t * (TAU / 1.8) + seed * 1.7);
  return v > 0 ? v ** 6 : 0;
}

function plusFx(
  g: G,
  b: BladeLine,
  L: number,
  look: PlusLook,
  t: number,
  seed: number,
  layer: FxLayer,
): void {
  const surge = surgeAt(t, seed) * look.surge;
  const start = bladeStart(L);
  if (layer === 'back') {
    const breathe = 0.85 + 0.15 * Math.sin(t * 6 + seed);
    g.save();
    toBlade(g, b);
    lay(g, hazeStrip(look.sheen, L), Math.min(1, look.sheenA * breathe + surge * 0.6));
    g.restore();
    if (look.smoke)
      // Black lightning smokes: curls rising off the blade.
      for (let i = 0; i < 6; i++) {
        const life = frac(t * 0.6 + hash(seed, i));
        const p = along(b, start + hash(seed, i, 7) * (L - start));
        const x = p.x + Math.sin(t * 3 + i * 2) * 1.5 * life;
        const y = p.y - life * 9;
        const a = ((1 - life) * 0.3).toFixed(2);
        const w = 1 + Math.round(life * 2) * P;
        dot(g, x - w / 2, y - w / 2, `rgba(14,6,22,${a})`, w);
        dot(g, x, y, `rgba(90,40,140,${a})`);
      }
    return;
  }
  const bolt = look.bolt;
  if (look.sheen === 'steel' || look.sheen === 'azure') {
    // The polished edge catching the light, brighter each +.
    g.save();
    toBlade(g, b);
    lay(
      g,
      edgeStrip(look.sheen === 'steel' ? '236,244,255' : '196,228,255', L),
      look.sheenA * 0.6 + surge * 0.4,
    );
    g.restore();
  }
  if (bolt) {
    const f = Math.floor(t * look.rate);
    g.save();
    toBlade(g, b);
    const n = look.bolts + (surge > 0.5 ? 1 : 0);
    for (let i = 0; i < n; i++) {
      const s = seed * 131 + f * 17 + i;
      if (bolt === 'static' && hash(s, 11) > look.chance + surge) continue;
      const arc = hash(s, 4) < look.leap + surge * 0.3;
      lay(
        g,
        boltStrip(
          bolt,
          L,
          Math.floor(hash(s, 12) * BOLT_VARIANTS),
          arc,
          i === 0 && bolt !== 'blue' && bolt !== 'static',
        ),
      );
    }
    if (bolt !== 'static') {
      const flare = hash(seed, f, 3) < 0.12 + surge * 0.5;
      lay(g, chargeStrip(bolt, L, flare ? 4 : Math.floor(hash(seed, f, 5) * 4)));
    }
    g.restore();
  }
  // A glint running up the polished edge, hilt to point.
  if (look.glint) {
    const u = frac(t / look.glint + hash(seed, 21));
    if (u < 0.3) {
      const k = start + (u / 0.3) * (L - start);
      const p = along(b, k);
      const q = along(b, k - 1);
      dot(g, q.x, q.y, 'rgba(255,255,255,0.45)');
      dot(g, p.x - P, p.y, 'rgba(255,255,255,0.7)', P * 3, P);
      dot(g, p.x, p.y - P, 'rgba(255,255,255,0.7)', P, P * 3);
      dot(g, p.x, p.y, '#ffffff');
    }
  }
  // Sparks thrown off, falling, more of them in a surge.
  const sparks = Math.floor(look.sparks + surge * 3);
  if (sparks) {
    const tone = bolt && bolt !== 'static' ? BOLTS[bolt] : BOLTS.static;
    for (let i = 0; i < sparks; i++) {
      const life = frac(t * 1.4 + hash(seed, i, 31));
      const cyc = Math.floor(t * 1.4 + hash(seed, i, 31));
      const k = start + hash(seed + cyc, i, 32) * (L - start + 1);
      const p = along(b, k);
      const vx = (hash(seed + cyc, i, 33) - 0.5) * 10;
      const vy = -2 - hash(seed + cyc, i, 34) * 4;
      const x = p.x + vx * life;
      const y = p.y + vy * life + life * life * 10;
      const col =
        life < 0.3 ? tone.fleck : life < 0.7 ? (bolt === 'black' ? '#c8a0ff' : tone.core) : tone.glow;
      dot(g, x, y, col);
      if (life < 0.6) dot(g, x - vx * 0.04, y - (vy + life * 20) * 0.04, tone.glow);
    }
  }
}

// ── elements ──

/** One mote circling the blade: where it is, and whether it is in front of it. */
interface Orbit {
  x: number;
  y: number;
  front: boolean;
  /** −1 behind … 1 in front: nearer motes are drawn larger and brighter. */
  depth: number;
}

/**
 * The `k`th of `n` motes spiralling up the blade at time `t`: round the blade at `spin` turns per
 * second, climbing it at `climb` lengths per second, `r` art pixels out.
 */
function orbit(
  b: BladeLine,
  L: number,
  k: number,
  n: number,
  t: number,
  spin: number,
  climb: number,
  r: number,
): Orbit {
  const a = (t * spin + k / n) * TAU;
  const u = frac(t * climb + k / n);
  const start = bladeStart(L);
  const p = along(b, start + u * (L - start + 2), Math.cos(a) * r);
  const depth = Math.sin(a);
  return { x: p.x, y: p.y - depth * r * 0.35, front: depth >= 0, depth };
}

type Tones = readonly [string, string, string, string];

const FIRE: Tones = ['#fff6d0', '#ffc04a', '#f0701e', '#a8301a'];
const POISON: Tones = ['#f0dcff', '#c08af0', '#8a4ad0', '#4a1a78'];
const STONE: Tones = ['#e8c890', '#b08a54', '#7a5a34', '#3e2c1c'];
const WATER: Tones = ['#f4feff', '#8ad8f4', '#3a96d0', '#1a4e86'];

const SPRITES = new Map<string, HTMLCanvasElement>();

/** A round blob lit from the upper right (highlight, light, body, shadow), `r` art pixels across. */
function blobSprite(tones: Tones, r: number): HTMLCanvasElement {
  const key = `blob:${tones[0]}:${r}`;
  let c = SPRITES.get(key);
  if (c) return c;
  const R = Math.ceil(r / P);
  c = canvas(R * 2 + 1, R * 2 + 1);
  const g = c.getContext('2d')!;
  for (let j = -R; j <= R; j++)
    for (let i = -R; i <= R; i++) {
      const dx = i * P;
      const dy = j * P;
      if (dx * dx + dy * dy > r * r + 0.1) continue;
      const lit = (dx * 0.6 - dy * 0.8) / Math.max(P, r);
      g.fillStyle = lit > 0.55 ? tones[0] : lit > 0.05 ? tones[1] : lit > -0.5 ? tones[2] : tones[3];
      g.fillRect(i + R, j + R, 1, 1);
    }
  SPRITES.set(key, c);
  return c;
}

/** Draws a sprite of half-pixels centred on (x, y), snapped to the grid. */
function sprite(g: G, c: HTMLCanvasElement, x: number, y: number): void {
  const hw = Math.floor(c.width / 2);
  const hh = Math.floor(c.height / 2);
  g.drawImage(c, Math.round(x / P) * P - hw * P, Math.round(y / P) * P - hh * P, c.width * P, c.height * P);
}

function blob(g: G, x: number, y: number, r: number, tones: Tones): void {
  sprite(g, blobSprite(tones, Math.round(r * 2) / 2), x, y);
}

/** Chunks of rock: their outline in pixels (x, y), lit per pixel. */
const ROCKS: readonly (readonly (readonly [number, number])[])[] = [
  [
    [0, 0],
    [1, 0],
    [2, 0],
    [-1, 1],
    [0, 1],
    [1, 1],
    [2, 1],
    [3, 1],
    [-1, 2],
    [0, 2],
    [1, 2],
    [2, 2],
    [3, 2],
    [0, 3],
    [1, 3],
    [2, 3],
  ],
  [
    [1, 0],
    [2, 0],
    [0, 1],
    [1, 1],
    [2, 1],
    [3, 1],
    [0, 2],
    [1, 2],
    [2, 2],
    [3, 2],
    [1, 3],
    [2, 3],
    [3, 3],
  ],
  [
    [0, 0],
    [1, 0],
    [0, 1],
    [1, 1],
    [2, 1],
    [-1, 2],
    [0, 2],
    [1, 2],
    [2, 2],
    [0, 3],
    [1, 3],
  ],
];

/** A tumbling stone, its face `i`, each of its pixels `big` half-pixels. */
function rockSprite(i: number, big: number): HTMLCanvasElement {
  const key = `rock:${i}:${big}`;
  let c = SPRITES.get(key);
  if (c) return c;
  c = canvas(6 * big, 5 * big);
  const g = c.getContext('2d')!;
  for (const [x, y] of ROCKS[i]!) {
    const lx = x - 1;
    const ly = y - 1.5;
    const lit = lx * 0.6 - ly * 0.8;
    g.fillStyle = lit > 1.4 ? STONE[0] : lit > 0 ? STONE[1] : lit > -1.4 ? STONE[2] : STONE[3];
    g.fillRect((x + 1) * big, y * big, big, big);
  }
  SPRITES.set(key, c);
  return c;
}

/** A film of the element's colour round the blade, and its thin line along the steel. */
function tintStrip(rgb: string, L: number, wide: number): Strip {
  return stripOf(`tint:${rgb}:${L}:${wide}`, L, 3, 4, (px) => {
    const start = bladeStart(L);
    for (let k = start - 0.5; k <= L + 0.5; k += P) {
      const w = wide + (hash(L * 7, Math.round(k * 2)) < 0.18 ? P : 0);
      px(k - w, -w, `rgba(${rgb},0.22)`, w * 2 + P);
      px(k - P, -P, `rgba(${rgb},0.42)`, P * 3);
    }
  });
}

function edgeStrip(rgb: string, L: number): Strip {
  return stripOf(`edge:${rgb}:${L}`, L, 1, 1, (px) => {
    for (let k = bladeStart(L); k <= L; k += P) px(k, 0, `rgba(${rgb},0.7)`);
  });
}

/** The element's own shimmer along the blade, flickering between a few variants. */
function shimmerStrip(el: ForgeElement, L: number, v: number): Strip {
  return stripOf(`shimmer:${el}:${L}:${v}`, L, 3, 5, (px) => {
    const start = bladeStart(L);
    const s = v * 389 + L;
    for (let k = start; k <= L + 1; k += P) {
      const i = Math.round(k * 2);
      const h = hash(s, i);
      switch (el) {
        case 'fire': {
          // Heat: a ragged orange fringe licking off both edges.
          const up = 1 + Math.round(hash(s, i, 1) * 3) * P;
          if (h < 0.55) px(k, -up, h < 0.2 ? 'rgba(255,200,90,0.6)' : 'rgba(240,110,40,0.4)');
          if (hash(s, i, 2) < 0.3) px(k, up * 0.7, 'rgba(200,70,30,0.35)');
          break;
        }
        case 'poison':
          // Venom shimmering violet on the steel.
          if (h < 0.35) px(k, (hash(s, i, 3) - 0.5) * 1, 'rgba(190,120,255,0.55)');
          break;
        case 'earth':
          // Grit stuck to the edge.
          if (h < 0.25) px(k, (hash(s, i, 3) < 0.5 ? -1 : 1) * P, h < 0.1 ? STONE[0] : STONE[2]);
          break;
        case 'water':
          // Wet steel: a lit run of water and beads along it.
          if (h < 0.5) px(k, -P, 'rgba(200,240,255,0.45)');
          if (h < 0.08) px(k, P, WATER[1]);
          break;
        case 'wind':
          // Moving air: pale ripples wavering either side.
          if (h < 0.4) px(k, Math.sin(k * 0.9 + v * 1.6) * 2, 'rgba(220,245,236,0.4)');
          break;
      }
    }
  });
}

/** How strongly each part of an element shows at level `lv` (1 … 8). */
interface ElStrength {
  tint: number;
  /** Loose particles of its matter at once, and whether they come only now and then. */
  particles: number;
  sparse: boolean;
  shimmer: boolean;
  /** Motes circling the blade, and how long their trails (0 … 1). */
  motes: number;
  trail: number;
  ground: number;
  full: boolean;
}

export function elStrength(lv: number): ElStrength {
  const l = Math.max(0, Math.min(8, Math.round(lv)));
  return {
    tint: l ? 0.3 + l * 0.0875 : 0,
    particles: [0, 1, 1, 2, 2, 3, 3, 4, 5][l]!,
    sparse: l <= 2,
    shimmer: l >= 3,
    motes: [0, 0, 0, 0, 0, 1, 2, 3, 4][l]!,
    trail: l >= 7 ? 1 : l >= 5 ? 0.4 : 0,
    ground: l >= 8 ? 1 : l >= 7 ? 0.55 : 0,
    full: l >= 8,
  };
}

/** The colour (r,g,b) of each element's film, glow and afterimage. */
const EL_RGB: Record<ForgeElement, string> = {
  fire: '240,120,40',
  poison: '150,70,220',
  earth: '170,130,80',
  water: '70,160,220',
  wind: '190,236,214',
};
const EL_CORE: Record<ForgeElement, string> = {
  fire: '#ffd890',
  poison: '#e2c4ff',
  earth: '#e8c890',
  water: '#e4f8ff',
  wind: '#f4fffa',
};

type ElFx = (g: G, b: BladeLine, L: number, st: ElStrength, t: number, seed: number, layer: FxLayer) => void;

/** Whether loose particle `i` shows in its current cycle (at low levels only now and then). */
const shows = (st: ElStrength, seed: number, i: number, cyc: number) =>
  !st.sparse || hash(seed, i * 13 + cyc, 77) < 0.45;

const fire: ElFx = (g, b, L, st, t, seed, layer) => {
  const n = st.motes;
  const tail = Math.round(2 + st.trail * 5);
  for (let k = 0; k < n; k++) {
    // A tail of embers where the fireball has been, drifting up as it cools.
    for (let j = tail; j >= 1; j--) {
      const o = orbit(b, L, k, n, t - j * 0.035, 0.9, 0.22, 5);
      if (o.front !== (layer === 'front')) continue;
      const cool = j / 7;
      const col = cool < 0.35 ? FIRE[1] : cool < 0.7 ? FIRE[2] : cool < 0.9 ? FIRE[3] : 'rgba(60,40,36,0.5)';
      dot(g, o.x, o.y - j * 0.4, col, j < 3 ? P * 2 : P);
    }
    const o = orbit(b, L, k, n, t, 0.9, 0.22, 5);
    if (o.front !== (layer === 'front')) continue;
    const flick = hash(seed, k, Math.floor(t * 20)) * 0.4;
    const r = (st.full ? 1.5 : 1) + o.depth * 0.4 + flick;
    // Flame licking up off the ball, then the ball itself with its white-hot heart.
    dot(g, o.x - P, o.y - r - 1, FIRE[2], P * 2, 1);
    dot(g, o.x, o.y - r - 1.5 - flick * 2, FIRE[1]);
    blob(g, o.x, o.y, r, FIRE);
  }
  if (layer !== 'front') return;
  const start = bladeStart(L);
  if (st.full)
    // Flames along the edge: short tongues flickering up.
    for (let j = 0; j < 5; j++) {
      const p = along(b, start + ((j + 0.5) / 5) * (L - start));
      const h = 1 + Math.round(hash(seed, j, Math.floor(t * 12)) * 4) * P;
      dot(g, p.x, p.y - h, FIRE[1], P, h - P);
      dot(g, p.x, p.y - h - P, FIRE[2]);
    }
  // Embers spat off the blade, rising and cooling.
  for (let i = 0; i < st.particles; i++) {
    const v = t * 0.9 + hash(seed, i, 1);
    if (!shows(st, seed, i, Math.floor(v))) continue;
    const life = frac(v);
    const p = along(b, start + hash(seed, i, 2) * (L - start + 1), (hash(seed, i, 3) - 0.5) * 4);
    dot(
      g,
      p.x + Math.sin(t * 4 + i) * life,
      p.y - life * 8,
      life < 0.35 ? FIRE[0] : life < 0.7 ? FIRE[1] : FIRE[3],
    );
  }
};

const poison: ElFx = (g, b, L, st, t, seed, layer) => {
  const start = bladeStart(L);
  if (layer === 'back') {
    // Wisps of miasma rising off the blade, curling as they thin: more the higher the level.
    const wisps = st.full ? 9 : Math.max(0, st.particles - 1) * 2;
    for (let i = 0; i < wisps; i++) {
      const life = frac(t * 0.5 + hash(seed, i));
      const p = along(b, start + hash(seed, i, 4) * (L - start));
      const x = p.x + Math.sin(t * 2.5 + i * 1.7 + life * 4) * (1 + life * 2.5);
      const y = p.y - life * 12;
      const a = Math.sin(life * Math.PI) * (st.full ? 0.6 : 0.4);
      const w = 2 + Math.round(life * 2) * P;
      dot(g, x - w / 2, y - w / 2, `rgba(110,40,170,${(a * 0.7).toFixed(2)})`, w);
      dot(g, x - P, y - P, `rgba(200,140,255,${a.toFixed(2)})`, 1);
    }
  }
  // Bubbles of venom circling the blade.
  const n = Math.min(3, st.motes);
  for (let k = 0; k < n; k++) {
    const o = orbit(b, L, k, n, t, 0.6, 0.15, 4.5);
    if (o.front !== (layer === 'front')) continue;
    for (let j = Math.round(1 + st.trail * 3); j >= 1; j--) {
      const q = orbit(b, L, k, n, t - j * 0.05, 0.6, 0.15, 4.5);
      dot(g, q.x, q.y, `rgba(150,80,220,${(0.5 - j * 0.1).toFixed(2)})`);
    }
    blob(g, o.x, o.y, 1 + o.depth * 0.4 + (st.full ? 0.5 : 0), POISON);
  }
  if (layer !== 'front') return;
  // Venom beading at the edge and dripping off.
  for (let i = 0; i < st.particles; i++) {
    const v = t * 0.7 + hash(seed, i, 9);
    if (!shows(st, seed, i, Math.floor(v))) continue;
    const life = frac(v);
    const p = along(b, start + hash(seed, i, 8 + Math.floor(v)) * (L - start), 1);
    // The bead swells, lets go and falls, stretching.
    if (life < 0.35) dot(g, p.x, p.y, life < 0.2 ? POISON[1] : POISON[2], P, life < 0.2 ? P : P * 2);
    else {
      const y = p.y + (life - 0.35) * (life - 0.35) * 26;
      dot(g, p.x, y, POISON[2], P, P * 2);
      dot(g, p.x, y, POISON[0]);
    }
  }
};

const earth: ElFx = (g, b, L, st, t, seed, layer) => {
  const n = st.motes;
  for (let k = 0; k < n; k++) {
    const o = orbit(b, L, k, n, t, 0.45, 0.12, 6);
    // Dust shed behind each stone.
    const dust = Math.round(2 + st.trail * 4);
    for (let j = 1; j <= dust; j++) {
      const q = orbit(b, L, k, n, t - j * 0.05, 0.45, 0.12, 6);
      if (q.front === (layer === 'front'))
        dot(g, q.x - P, q.y + j * 0.3, `rgba(176,140,90,${(0.5 - j * 0.07).toFixed(2)})`, j < 3 ? P * 2 : P);
    }
    if (o.front !== (layer === 'front')) continue;
    // Each stone tumbles: a new face every few frames; the near ones bigger.
    const face = (k + Math.floor(t * 4 + hash(seed, k) * 3)) % ROCKS.length;
    sprite(g, rockSprite(face, o.depth > 0.3 && k % 2 === 0 ? 2 : 1), o.x, o.y);
  }
  if (layer !== 'front') return;
  // Grit trickling off the blade, and now and then a chip.
  const start = bladeStart(L);
  for (let i = 0; i < st.particles + (st.full ? 2 : 0); i++) {
    const v = t * 1.1 + hash(seed, i, 5);
    if (!shows(st, seed, i, Math.floor(v))) continue;
    const life = frac(v);
    const p = along(
      b,
      start + hash(seed, i, 6 + Math.floor(v)) * (L - start + 1),
      (hash(seed, i, 7) - 0.5) * 3,
    );
    const y = p.y + life * life * 9;
    if (i === 0 && st.particles >= 3) dot(g, p.x, y, STONE[1], P * 2, P * 2);
    else dot(g, p.x, y, life < 0.5 ? STONE[1] : STONE[2]);
  }
};

const water: ElFx = (g, b, L, st, t, seed, layer) => {
  // Ribbons of water spiralling round the blade, each a stream of drops.
  const n = Math.min(3, st.motes);
  const len = Math.round(6 + st.trail * 7 + (st.full ? 2 : 0));
  for (let k = 0; k < n; k++)
    for (let j = len; j >= 0; j--) {
      const o = orbit(b, L, k, n, t - j * 0.03, 0.7, 0.18, 5);
      if (o.front !== (layer === 'front')) continue;
      const head = j === 0;
      const tone = head ? WATER[0] : j < 3 ? WATER[1] : j < 8 ? WATER[2] : WATER[3];
      const w = head ? 2 : j < 6 ? 1.5 : j < 10 ? 1 : P;
      dot(g, o.x - w / 2, o.y - w / 2, tone, w);
      // The lit top edge of the stream.
      if (!head && j < 7 && o.depth > 0) dot(g, o.x, o.y - w / 2, WATER[0]);
    }
  if (layer !== 'front') return;
  // Droplets flung off, arcing and falling.
  const start = bladeStart(L);
  for (let i = 0; i < st.particles; i++) {
    const v = t * 0.8 + hash(seed, i, 3);
    if (!shows(st, seed, i, Math.floor(v))) continue;
    const life = frac(v);
    const cyc = Math.floor(v);
    const p = along(b, start + hash(seed + cyc, i, 4) * (L - start + 1), (hash(seed, i, 5) - 0.5) * 4);
    const x = p.x + (hash(seed + cyc, i, 6) - 0.5) * life * 8;
    const y = p.y - 2 * life + life * life * 10;
    dot(g, x, y, WATER[1]);
    dot(g, x, y - P, WATER[0]);
  }
};

const wind: ElFx = (g, b, L, st, t, seed, layer) => {
  // Streaks of wind whipping round the blade: thin pale arcs, brightest at the head.
  const n = Math.min(3, st.motes);
  const len = Math.round(6 + st.trail * 6);
  for (let k = 0; k < n; k++)
    for (let j = len * 2; j >= 0; j -= 2) {
      const o = orbit(b, L, k, n, t - j * 0.01, 1.4, 0.3, 6 + (k % 2) * 1.5);
      if (o.front !== (layer === 'front')) continue;
      const a = ((1 - j / (len * 2 + 1)) * (o.front ? 0.95 : 0.55)).toFixed(2);
      const w = j < 8 ? 1 : P;
      dot(g, o.x - w / 2, o.y - w / 2, j < 5 ? `rgba(248,255,252,${a})` : `rgba(170,224,200,${a})`, w);
    }
  // Leaves caught in it, tumbling (from level 6).
  const leaves = st.motes >= 2 ? (st.full ? 2 : 1) : 0;
  for (let k = 0; k < leaves; k++) {
    const o = orbit(b, L, k + 0.5, 2, t + seed, 0.8, 0.2, 7);
    if (o.front !== (layer === 'front')) continue;
    const flip = Math.floor(t * 6 + k) % 2;
    dot(g, o.x, o.y, '#9ac860', flip ? P * 3 : P * 2, flip ? P * 2 : P * 3);
    dot(g, o.x + P, o.y, '#5a8a3a');
  }
  if (layer !== 'front') return;
  // Gusts streaking past the blade, sideways.
  const start = bladeStart(L);
  for (let i = 0; i < st.particles; i++) {
    const v = t * 1.6 + hash(seed, i, 41);
    if (!shows(st, seed, i, Math.floor(v))) continue;
    const life = frac(v);
    const p = along(b, start + hash(seed + Math.floor(v), i, 42) * (L - start), 0);
    const x = p.x - 6 + life * 12;
    const y = p.y - 2 + hash(seed, i, 43) * 3;
    const a = Math.sin(life * Math.PI) * 0.8;
    dot(g, x, y, `rgba(236,250,244,${a.toFixed(2)})`, 2, P);
    dot(g, x - 2, y, `rgba(180,226,206,${(a * 0.6).toFixed(2)})`, 1.5, P);
  }
};

const ELEMENT_FX: Record<ForgeElement, ElFx> = { fire, poison, earth, water, wind };

/** The element's film and shimmer along the blade (drawn in the blade's space). */
function elStrips(g: G, el: ForgeElement, L: number, st: ElStrength, t: number, layer: FxLayer): void {
  if (layer === 'back') {
    const breathe = 0.85 + 0.15 * Math.sin(t * 3);
    lay(g, tintStrip(EL_RGB[el], L, st.full ? 2 : st.motes ? 1.5 : 1), st.tint * breathe);
    // The full poison wraps the blade in a purple aura.
    if (st.full && el === 'poison') lay(g, tintStrip('120,50,190', L, 2.5), 0.7 + 0.3 * Math.sin(t * 3));
    return;
  }
  lay(g, edgeStrip(EL_RGB[el], L), st.tint * 0.8);
  if (st.shimmer)
    lay(g, shimmerStrip(el, L, Math.floor(t * (el === 'fire' ? 12 : 8)) % 4), st.full ? 1 : 0.7);
}

// ── the ground ──

type GroundKind = ForgeElement | 'red' | 'black';
/** The ground glow's half width and height, art pixels, and its phases. */
const GRX = 12;
const GRY = 4.5;
const GPHASES = 8;

/** A glow on the ground under the fighter, one of `GPHASES` phases of its loop. */
function groundSprite(kind: GroundKind, ph: number): HTMLCanvasElement {
  const key = `ground:${kind}:${ph}`;
  let c = SPRITES.get(key);
  if (c) return c;
  const W = Math.round(GRX / P);
  const H = Math.round(GRY / P);
  c = canvas(W * 2 + 1, H * 2 + 1);
  const g = c.getContext('2d')!;
  const u = ph / GPHASES;
  for (let j = -H; j <= H; j++)
    for (let i = -W; i <= W; i++) {
      const d = Math.hypot(i / W, j / H);
      if (d > 1) continue;
      const n = hash(i * 31 + ph, j * 17, kind.length);
      const ang = Math.atan2(j / H, i / W);
      let col: string | null = null;
      switch (kind) {
        case 'fire':
          // Embers' glow: bands, ragged at their edges and flickering.
          col =
            d < 0.35 + n * 0.12
              ? 'rgba(255,186,90,0.5)'
              : d < 0.65 + n * 0.12
                ? 'rgba(240,110,40,0.34)'
                : n < 0.5
                  ? 'rgba(170,60,24,0.2)'
                  : null;
          break;
        case 'poison':
          // A pool of miasma, mottled, with bubbles rising in turn.
          col =
            hash(i, j, 5) < 0.04 && hash(i, j, ph) < 0.5
              ? 'rgba(210,160,255,0.6)'
              : d < 0.55 + n * 0.15
                ? 'rgba(90,30,140,0.32)'
                : 'rgba(60,20,90,0.18)';
          break;
        case 'earth':
          // Dust and cracks radiating, specks of grit hopping.
          col =
            Math.abs(frac((ang / TAU) * 5 + 0.1) - 0.5) < 0.04 && d > 0.25
              ? 'rgba(52,36,22,0.6)'
              : hash(i, j, ph) < 0.04
                ? 'rgba(226,196,146,0.75)'
                : d < 0.8
                  ? 'rgba(160,122,74,0.3)'
                  : null;
          break;
        case 'water': {
          // Ripples spreading from under the feet.
          const r1 = frac(u);
          const r2 = frac(u + 0.5);
          const ring = (r: number) => Math.abs(d - r) < 0.06 + (1 - r) * 0.04;
          col =
            ring(r1) || ring(r2)
              ? `rgba(180,230,250,${(0.7 * (1 - Math.min(r1, r2) * 0.5)).toFixed(2)})`
              : 'rgba(50,130,200,0.14)';
          break;
        }
        case 'wind': {
          // Dashes of dust whirling round.
          const a = frac(ang / TAU + u / 3);
          col =
            d > 0.65 && frac(a * 6) < 0.3
              ? 'rgba(226,248,240,0.6)'
              : d < 0.6 && n < 0.12
                ? 'rgba(206,234,220,0.35)'
                : null;
          break;
        }
        case 'red':
          col = d < 0.45 + n * 0.1 ? 'rgba(230,50,34,0.34)' : n < 0.6 ? 'rgba(160,24,20,0.2)' : null;
          if (n < 0.025 && hash(i, j, ph) < 0.5) col = 'rgba(255,200,170,0.8)';
          break;
        case 'black':
          col = d < 0.6 + n * 0.12 ? 'rgba(18,6,28,0.45)' : 'rgba(40,14,60,0.25)';
          if (d > 0.85 && hash(i, j, ph) < 0.18) col = 'rgba(170,110,255,0.6)';
          break;
      }
      if (!col) continue;
      g.fillStyle = col;
      g.fillRect(i + W, j + H, 1, 1);
    }
  SPRITES.set(key, c);
  return c;
}

function ground(g: G, kind: GroundKind, at: Pt, a: number, t: number, rate: number): void {
  const ph = Math.floor(t * rate) % GPHASES;
  const was = g.globalAlpha;
  g.globalAlpha = was * Math.min(1, a);
  sprite(g, groundSprite(kind, ph), at.x, at.y);
  g.globalAlpha = was;
}

// ── the afterimage of a swing ──

interface Snap {
  hx: number;
  hy: number;
  dx: number;
  dy: number;
  L: number;
  at: number;
}
interface Trail {
  last: Snap;
  ghosts: Snap[];
  seen: number;
}
const TRAILS = new Map<number, Trail>();
/** How long a ghost lingers, seconds. */
const GHOST_LIFE = 0.16;

/** Remembers where the blade was; returns the ghosts its swing has left. */
function swing(key: number, b: BladeLine, L: number, t: number): Snap[] {
  let tr = TRAILS.get(key);
  const now: Snap = { hx: b.hx, hy: b.hy, dx: b.dx, dy: b.dy, L, at: t };
  if (!tr || t < tr.seen || t - tr.seen > 0.5) {
    if (TRAILS.size > 400) for (const [k, v] of TRAILS) if (t - v.seen > 2 || t < v.seen) TRAILS.delete(k);
    tr = { last: now, ghosts: [], seen: t };
    TRAILS.set(key, tr);
    return tr.ghosts;
  }
  tr.seen = t;
  const turn = Math.abs(
    Math.atan2(tr.last.dx * b.dy - tr.last.dy * b.dx, tr.last.dx * b.dx + tr.last.dy * b.dy),
  );
  if (turn > 0.18 || Math.hypot(b.hx - tr.last.hx, b.hy - tr.last.hy) > 2.5) {
    const old = tr.last;
    // The arc between where it was and where it is now, swept in light.
    for (const k of [0, 0.5])
      tr.ghosts.push({
        hx: old.hx + (b.hx - old.hx) * k,
        hy: old.hy + (b.hy - old.hy) * k,
        dx: old.dx + (b.dx - old.dx) * k,
        dy: old.dy + (b.dy - old.dy) * k,
        L: Math.round(old.L + (L - old.L) * k),
        at: t,
      });
    tr.last = now;
  }
  while (tr.ghosts.length && (t - tr.ghosts[0]!.at > GHOST_LIFE || tr.ghosts.length > 6)) tr.ghosts.shift();
  return tr.ghosts;
}

// ── drawing it all ──

export interface ForgeFxOptions {
  /** The ground under the fighter in the frame's art pixels, for the glow there (left out: none). */
  ground?: Pt;
  /** Leave an afterimage on swings (remembers the last blade per `seed`; the studio's cards leave it off). */
  trail?: boolean;
}

/**
 * Draws a forged weapon's effects along `blades` (art pixels of the frame `g` is set up to draw in).
 * A weapon held behind the body has all of it drawn in the back pass.
 */
export function drawForgeFx(
  g: G,
  blades: readonly BladeLine[],
  f: WeaponForge,
  t: number,
  layer: FxLayer,
  seed = 0,
  opts: ForgeFxOptions = {},
): void {
  if (!forgeShows(f)) return;
  const look = f.plus > 0 ? plusLook(f.plus) : null;
  const el = f.el && f.lv > 0 ? f.el : null;
  const st = el ? elStrength(f.lv) : null;
  g.save();
  g.imageSmoothingEnabled = false;
  if (layer === 'back' && opts.ground && blades.length) {
    if (look?.ground) ground(g, look.ground, opts.ground, look.groundA, t, 10);
    if (el && st!.ground) ground(g, el, opts.ground, st!.ground, t, el === 'water' ? 6 : 10);
  }
  blades.forEach((b, i) => {
    const L = Math.max(4, Math.round(b.len));
    const s = seed * 7 + i * 3 + 1;
    const passes: FxLayer[] = b.behind ? (layer === 'back' ? ['back', 'front'] : []) : [layer];
    if (opts.trail && passes[0] === 'back') {
      const ghosts = swing(seed * 4 + i, b, L, t);
      const tone =
        look?.bolt && look.bolt !== 'static'
          ? {
              col: BOLTS[look.bolt].rim ?? BOLTS[look.bolt].glow,
              core: look.bolt === 'black' ? '#c8a0ff' : BOLTS[look.bolt].core,
            }
          : el && st!.motes
            ? { col: `rgba(${EL_RGB[el]},0.55)`, core: EL_CORE[el] }
            : look && f.plus >= 4
              ? { col: 'rgba(130,190,255,0.5)', core: '#e4f2ff' }
              : null;
      if (tone)
        for (const gh of ghosts) {
          g.save();
          g.transform(gh.dx, gh.dy, -gh.dy, gh.dx, gh.hx, gh.hy);
          lay(g, ghostStrip(tone.col, tone.core, gh.L), (1 - (t - gh.at) / GHOST_LIFE) * 0.75);
          g.restore();
        }
    }
    for (const pass of passes) {
      // The element round the blade, then the + over everything.
      if (el) {
        g.save();
        toBlade(g, b);
        elStrips(g, el, L, st!, t + s, pass);
        g.restore();
        ELEMENT_FX[el](g, b, L, st!, t, s, pass);
      }
      if (look) plusFx(g, b, L, look, t, s, pass);
    }
  });
  g.restore();
}

// ── the moment a forge succeeds, on the fighter ──

/** A forging just struck, for the burst over the player (render time). */
export interface ForgeBurst {
  at: number;
  what: 'plus' | 'element';
  ok: boolean;
  level: number;
  el: ForgeElement | null;
}

let lastBurst: ForgeBurst | null = null;

/** The forge panel calls this when a strike lands, with the world's time. */
export function noteForgeBurst(b: ForgeBurst): void {
  lastBurst = b;
}

/** How long the burst plays, seconds. */
const BURST_LIFE = 0.9;

/** The colour of a burst: the + tier's lightning, or the element's. */
function burstTones(b: ForgeBurst): { glow: string; core: string } {
  if (b.what === 'element' && b.el) return { glow: `rgb(${EL_RGB[b.el]})`, core: EL_CORE[b.el] };
  const look = plusLook(b.level);
  if (look.bolt && look.bolt !== 'static')
    return { glow: look.bolt === 'black' ? '#8a5ad8' : BOLTS[look.bolt].glow, core: BOLTS[look.bolt].core };
  return { glow: '#d9b46c', core: '#fff4d0' };
}

/**
 * The burst over a fighter who has just forged well: a ring of sparks flung out, a column of light
 * and motes drifting up. (`x`, `y`) is the chest, in the canvas's units; `s` the figure's scale.
 */
export function drawForgeBurst(g: G, x: number, y: number, s: number, t: number): void {
  const b = lastBurst;
  if (!b || !b.ok) return;
  const age = t - b.at;
  if (age < 0 || age > BURST_LIFE) return;
  const u = age / BURST_LIFE;
  const { glow, core } = burstTones(b);
  const px = s * P;
  const put = (dx: number, dy: number, col: string, w = 1, h = w) => {
    g.fillStyle = col;
    g.fillRect(Math.round((x + dx * s) / px) * px, Math.round((y + dy * s) / px) * px, w * px, h * px);
  };
  g.save();
  // The column of light, thinning as it fades.
  if (u < 0.45) {
    g.globalAlpha = (1 - u / 0.45) * 0.5;
    const w = Math.max(1, Math.round((1 - u / 0.45) * 6));
    put(-w / 4, -30, glow, w, 60 * (1 - u));
    g.globalAlpha = (1 - u / 0.45) * 0.8;
    put(-0.25, -30, core, 1, 60 * (1 - u));
  }
  // A ring of sparks flung out and falling.
  g.globalAlpha = 1 - u * u;
  const n = 14;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + hash(i, 3) * 0.3;
    const r = 3 + u * (14 + hash(i, 5) * 6);
    const dx = Math.cos(a) * r;
    const dy = Math.sin(a) * r * 0.55 + u * u * 10;
    put(dx, dy, u < 0.35 ? core : glow, u < 0.5 ? 2 : 1);
  }
  // Motes drifting up.
  for (let i = 0; i < 6; i++) {
    const d = (hash(i, 11) - 0.5) * 16;
    put(d + Math.sin(u * 6 + i) * 1.5, 6 - u * (16 + hash(i, 12) * 10), i % 2 ? core : glow);
  }
  g.restore();
}
